import { selectors as cartSelectors } from 'core/use-cases/cart';
import { getProductCartItem } from 'core/use-cases/helpers/';
import { selectors as userSelectors } from 'core/use-cases/user';
import { useSelector } from 'core/use-cases/utilities/';

import type { Showcases } from 'core/domains';
import type { CatalogItem } from 'core/domains/catalog';
import type { SelectorReturnFn } from 'core/use-cases/utilities';

export function useShowcasesSelector() {
  const {
    updatedAt,
    status,
    isRunning,
    availableShowcases,
    data: allFetchedShowcases,
  } = useSelector((state) => state.showcases);

  const { data: cartData } = cartSelectors.useCartSelector();
  const itemAddCartError = userSelectors.useItemAddCartErrorSelector();
  const allShowcasesAreFetched = !availableShowcases.find(({ isFetched }) => !isFetched);
  const formattedShowcasesData = allFetchedShowcases
    ?.map((showcase) => {
      return {
        order: availableShowcases.find(({ tag }) => tag === showcase?.tagName)?.order || 0,
        tagName: showcase?.tagName,
        description: showcase?.description ?? '',
        name: showcase?.name ?? '',
        data:
          showcase?.data.map((showcasesItem: CatalogItem) => {
            const { productOrigin, productPosition, quantity } = getProductCartItem(
              showcasesItem.productCode,
              cartData,
            );

            return {
              ...showcasesItem,
              productOrigin,
              productPosition,
              quantity: quantity || 0,
              isAvailable: itemAddCartError?.includes(showcasesItem?.productCode)
                ? false
                : showcasesItem?.isAvailable,
            };
          }) || [],
      };
    })
    .sort((a, b) => a.order - b.order);
  const allFormattedEmptyOrUnavailable = formattedShowcasesData?.every(({ data }) => !data.length);

  return {
    data: {
      formattedShowcasesData,
      allFetchedShowcases,
      allShowcasesAreFetched,
      availableShowcases,
      allFormattedEmptyOrUnavailable,
    },
    status,
    updatedAt: updatedAt || 0,
    isLoading: isRunning,
  };
}

export const useShowcasesAvailableSelector = (): SelectorReturnFn<
  Showcases['availableShowcases']
> => {
  const { availableShowcases, status, updatedAt } = useSelector((state) => state.showcases);
  return { data: availableShowcases || [], status, updatedAt: updatedAt || 0 };
};
